<template>
    <v-layout column class="conversation-view" data-qe-id="messenger-conversation-view">
        <v-layout column>
            <v-flex v-if="showParticipantsAutocomplete" class="px-3 py-1">
                <v-autocomplete
                        dense clearable
                        v-model="draftParticipantsList"
                        :label="iamConstants.ADD_PARTICIPANTS"
                        :items="typeAheadResults"
                        :loading="loadingTypeAhead"
                        :search-input.sync="typeAheadSearch"
                        :hint="iamConstants.TYPEAHEAD_PROMPT"
                        :no-data-text="typeAheadPlaceholderText"
                        data-qe-id="messenger-page_search-people_auto-complete"
                        return-object
                        item-text="name"
                        item-avatar="profileImageUrl">
                </v-autocomplete>
            </v-flex>
            <v-flex v-else class="conversation-view__chat-title--wrapper">
                <v-layout row class="conversation-view__chat-title">
                    <v-flex class="conversation-view__chat-menu--mobile flex-grow-overwrite">
                       <conversation-panel-menu
                               :conversation="conversation"
                               :on-manage-group="onManageGroup"
                               :display-right="true"
                               :on-deleted="onDeleted">
                       </conversation-panel-menu>
                    </v-flex>
                    <v-flex class="conversation-view__chat-title--name" xs8>
                        {{title}}
                    </v-flex>
                    <v-flex class="text text-right">
                        <v-layout justify-end>
                            <v-flex class="flex-grow-overwrite conversation-view__chat-menu--desktop">
                                <conversation-panel-menu
                                        :conversation="conversation"
                                        :on-manage-group="onManageGroup"
                                        :on-deleted="onDeleted">
                                </conversation-panel-menu>
                            </v-flex>
                            <v-flex class="flex-grow-overwrite">
                                <v-btn flat icon color="ypo_cta" @click="closeChat"
                                       data-qe-id="messenger-page_close-chat_btn">
                                    <i class="fas fa-times"></i>
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-flex>
            <v-flex class="">
                <v-layout class="conversation-history">
                    <v-flex xs12 class="inner-scrollbar" id="messageList">
                        <v-layout column>
                            <v-flex xs12 v-for="(event, index) in events"
                                    :key="index">
                                <conversation-message v-if="isMessage(event)"
                                                      :message="event"
                                                      :display-date="index === 0"
                                                      :author="authorOf(event)"></conversation-message>
                                <conversation-marker v-else-if="isMarker(event)" :marker="event"></conversation-marker>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-flex>
            <v-flex class="conversation-actions" data-qe-id="conversation-actions">
                <v-layout row wrap>
                    <v-flex xs12 pl-2 class="conversation-actions__send__block">
                        <v-textarea auto-grow rows="1"
                                    v-model="draftMessage"
                                    hide-details
                                    :class="['inner-scrollbar', 'conversation__input', draftMessage ? '' : 'hide_send']"
                                    data-qe-id="conversation-message-input">
                        </v-textarea>
                        <v-flex  class="conversation-actions__send">
                        <v-icon @click="sendMessageForm">send</v-icon>
                    </v-flex>
                    </v-flex>
                    
                    <div>
                        <!-- TODO enable media -->
                        <!-- <v-btn icon>
                          <v-icon>photo_size_select_actual</v-icon>
                        </v-btn>
                        <v-btn icon>
                          <v-icon>attach_file</v-icon>
                        </v-btn> -->
                        <!-- TODO this needs to pop out an emoji picker -->
                        <!-- <v-btn icon>
                          <v-icon>tag_faces</v-icon>
                        </v-btn> -->
                    </div>

                </v-layout>
            </v-flex>
        </v-layout>
    </v-layout>
</template>

<script>
    import { appCommonConstants } from "@/components/appCommon.constants";
    import ConversationMessage from './ConversationMessage';
    import ConversationMarker from './ConversationMarker';
    import { iamConstants } from '../iam.constant';
    import {
        conversationTitle,
        isValidConversationId,
        isGroupConversation,
        getMessagesFromConversation,
    } from '../iam.utils';
    import { mapActions, mapGetters, mapMutations } from 'vuex';
    import { storeAction } from '../../../store/storeActions.constant';
    import { storeGetters } from '../../../store/storeGetters.constant';
    import { storeMutation } from '../../../store/storeMutations.constant';
    import ConversationPanelMenu from "./ConversationPanelMenu";

    export default {
        name: 'ConversationPanel',
        components: {
            ConversationPanelMenu,
            ConversationMessage,
            ConversationMarker,
        },
        props: {
            conversation: {
                type: Object,
                required: true,
            },
            setActiveConversationPriorToSend: {
                type: Function,
                required: true,
            },
            startSendingReadMarkers: {
                type: Boolean,
            },
            // This can't be handled by an event listener because it's expected
            // that the component emitting this event will be deleted when the
            // conversation no longer exists.
            onDeleted: {
                type: Function,
                required: false,
            },
            onSendMessage: {
                type: Function,
                required: false,
            },
            onManageGroup: {
                type: Function,
                required: false,
            },
            closeChatAction: {
                type: Function,
            },
        },
        created() {
            const p = Object.keys(this.conversation.participants);
            this.showParticipantsAutocomplete = !p.length;
        },
        mounted() {
            this.scrollToBottom();
        },
        watch: {
            events() {
                this.scrollToBottom();
                //setting seen for the latest event when on active conversation
                if (this.conversation && this.startSendingReadMarkers) {
                    this.sendReadNotification(this.conversation);
                }
            },
            typeAheadSearch(searchText) {
                this.loadingTypeAhead = true;
                clearTimeout(this.timeoutForTypeAhead);
                this.timeoutForTypeAhead = setTimeout(() => {
                    if (searchText && searchText.length > 2) {
                        this.setMemberDirectorySearchText(searchText);
                        this.initiatePeopleSearch()
                            .then((resp) => {
                                if (resp && resp.data) {
                                    this.typeAheadResults = resp.data.map(sr => ({
                                        ...sr,
                                        name: `${sr.preferredName || sr.firstName} ${sr.lastName}`,
                                    }));
                                }
                            })
                            .catch(() => {
                                this.typeAheadResults = [];
                            })
                            .finally(() => {
                                this.loadingTypeAhead = false;
                            });
                    } else {
                        this.loadingTypeAhead = false;
                        this.typeAheadResults = [];
                    }
                }, 200);
            },
        },
        data() {
            return {
                iamConstants,
                appCommonConstants,
                currentDialog: null,
                // draftParticipantsList: [],
                draftParticipantsList: null, // groups not enabled
                showParticipantsAutocomplete: false,
                draftMessage: '',
                disableSend: false,
                isScrolledToBottom: true,
                typeAheadSearch: '',
                loadingTypeAhead: false,
                timeoutForTypeAhead: null,
                typeAheadResults: [],
            };
        },
        computed: {
            ...mapGetters('messenger', {
                messengerUsername: storeGetters.messengerUsername,
                chatParticipantProfiles: storeGetters.chatParticipantProfiles,
            }),
            typeAheadPlaceholderText() {
                if (this.loadingTypeAhead) {
                    return iamConstants.LOADING_TYPEAHEAD_RESULTS;
                } else if (this.typeAheadSearch) {
                    return iamConstants.NO_RESULTS;
                }
                return iamConstants.TYPEAHEAD_PROMPT;
            },
            title() {
                let title = '';
                if (this.conversation && this.conversation.participants) {
                    title = !isGroupConversation(this.conversation)
                        ? conversationTitle(Object.keys(this.conversation.participants)
                            .filter(k => k !== this.messengerUsername)
                            .map(k => this.conversation.participants[k]))
                        : this.conversation.name;
                }
                return title;
            },
            events() {
                const events = [];
                const rawEvents = (this.conversation && this.conversation.events) || [];

                let lastDateTs = null;
                rawEvents.forEach((e) => {
                    if (!this.isDisplayable(e)) return;

                    const eventDate = new Date(e.ts);
                    eventDate.setHours(0, 0, 0, 0);
                    const dateTs = eventDate.getTime();
                    if (!lastDateTs) {
                        lastDateTs = dateTs;
                    } else if (dateTs !== lastDateTs) {
                        events.push({
                            type: 'marker:date_transition',
                            ts: dateTs,
                        });
                        lastDateTs = dateTs;
                    }

                    events.push(e);
                });
                return events;
            },
        },
        methods: {
            ...mapMutations('messenger', {
                insertFailedMessage: storeMutation.insertFailedMessage,
            }),
            ...mapMutations('memberDirectory', {
                setMemberDirectorySearchText: storeMutation.setMemberDirectorySearchText,
            }),
            ...mapActions('messenger', {
                sendMessage: storeAction.sendMessage,
                deleteConversation: storeAction.deleteConversation,
                setRoomReadMarkers: storeAction.setRoomReadMarkers,
            }),
            ...mapActions('memberDirectory', {
                initiatePeopleSearch: storeAction.initiatePeopleSearch,
            }),
            closeChat() {
                if (this.closeChatAction) {
                    this.closeChatAction();
                } else {
                    this.$router.go(-1);
                }
            },
            isMessage(event) {
                return !!(event && event.type === 'message');
            },
            isMarker(event) {
                return !!(event && /marker:.+/.test(event.type));
            },
            isDisplayable(event) {
                return this.isMessage(event) || this.isMarker(event);
            },
            authorOf(message) {
                if (!message || !message.author) return {};
                return this.chatParticipantProfiles[message.author] || {};
            },
            sendMessageForm() {
                if (this.draftMessage.trim().length > 0) {
                    if (!Object.keys(this.conversation.participants).length) {
                        const p = {};
                        // this.draftParticipantsList.forEach((x) => { p[x.id] = x; });
                        p[this.draftParticipantsList.id] = this.draftParticipantsList; // groups not enabled
                        this.conversation.participants = p;
                    }

                    //adding this to remove the notification count display for conversation that is selected on load
                    if (isValidConversationId(this.conversation.id)) {
                        this.setActiveConversationPriorToSend(this.conversation.id);
                    } else {
                        this.setActiveConversationPriorToSend(iamConstants.DRAFT_CHAT_ID);
                    }

                    this.disableSend = true;
                    this.sendMessage({
                        conversation: this.conversation,
                        messageText: this.draftMessage,
                    })
                        .then((conversationId) => {
                            if (this.onSendMessage) this.onSendMessage({ conversationId });
                        })
                        .catch((err) => {
                            const msgs = this.events.filter(e => this.isMessage(e));
                            const previousMessageId =
                                (msgs && msgs.length &&
                                    msgs[msgs.length - 1].id) ||
                                null;
                            this.insertFailedMessage({
                                conversationId: this.conversation.id,
                                previousMessageId,
                                message: {
                                    author: this.messengerUsername,
                                    messageText: this.draftMessage,
                                },
                            });
                            throw err;
                        })
                        .finally(() => {
                            this.showParticipantsAutocomplete = false;
                            this.disableSend = false;
                            this.draftMessage = '';
                            this.scrollToBottom();
                            document.getElementById('conversation-message-input').style.height = "auto";
                        });
                }
            },
            initDeleteConversation() {
                if (!isValidConversationId(this.conversation.id)) {
                    return;
                }
                this.showDeleteConversationDialog = true;
            },
            confirmDeleteConversation() {
                this.deleteConversation({ conversationId: this.conversation.id }).then(() => {
                    this.showDeleteConversationDialog = false;
                    if (this.onDeleted) this.onDeleted();
                });
            },
            scrollToBottom() {
                const objDiv = document.getElementById("messageList");
                if (objDiv) {
                    objDiv.scrollTop = objDiv.scrollHeight;
                }
            },
            sendReadNotification(ac) {
                const messageList = getMessagesFromConversation(ac);
                if (messageList && messageList.length) {
                    const lastEvent = messageList[messageList.length - 1];
                    this.setRoomReadMarkers({ roomId: ac.id, eventId: lastEvent.id });
                }
            },
        },
    };
</script>

<style scoped lang="scss">
    @import '../../../common/styles/_colors.scss';
    @import '../../../common/styles/_responsive.scss';
    @import '../../../common/styles/_commonClasses.scss';

    .conversation-view__chat-title--wrapper {
        max-height: 50px;
    }

    .conversation-view {
        margin-right: -1px;
        background: url(../../../assets/triangle.png);
        height: 100%;

        &__chat {

            &-title {
                color: $ypo-blue;
                background: $ypo-open-blue-gray;
                font-size: 16px;
                min-height: 51px;

                @include breakpoint(xs) {
                    font-size: 14px;
                }

                &--name {
                    font-weight: 600;
                    padding: 12px 0 10px 20px;
                    text-align: left;

                    @include breakpoint(xs) {
                        text-align: center;
                        padding: 15px;
                    }
                }
            }

            &-menu {
                &--mobile {
                    display: none;
                    @include breakpoint(xs) {
                        display: block;
                    }
                }

                &--desktop {
                    @include breakpoint(xs) {
                        display: none;
                    }
                }
            }
        }
         @include breakpoint(xs) {
             margin-right: 0px;
         }
    }

    .conversation__input {
        padding: 10px 5px;
        max-height: 80px;
        overflow: auto;
    }

    .conversation-history {
        align-items: stretch;
        height: 100%;

        & > * {
            flex-grow: 1;
        }

        padding: 10px 10px 0;

        .inner-scrollbar {
            max-height: 60vh;
            min-height: 60vh;
            margin-right: 0;
            overflow: auto;
        }
    }

    .conversation-actions {
        border-left: 1px solid rgba($ypo-blue, 0.09);
        position: relative;

        .layout {
            background: $white;
            box-shadow: 0 0 2px 0 rgba($black, 0.18);
            bottom: 0;
            width: 100%;
        }

        &__row {
            text-align: right;
            .v-btn {
                margin: 10px 0 0 0;
            }
        }
        &__send {
            position: absolute;
            padding: 14px 10px;
            right: 0px;
            top: 0px;
            z-index: 2;
            .v-icon {
                color: $ypo-cta;
            }
            &__block {
                position: relative;
                padding-right: 50px;
            }
        }
    }
</style>

<style lang="scss">
    @import '../../../common/styles/_responsive.scss';

    .conversation-history .vue-recycle-scroller__item-view.hover {
        cursor: unset;
    }

    .conversation-history .vue-recycle-scroller__item-view {
        width: auto !important;
    }

    .hide_send {
        .v-input__append-inner {
            @include breakpoint(xs) {
                display: none !important;
            }
        }
    }

    .conversation__input {
        .v-icon {
            color: $ypo_cta !important;
        }
        @include breakpoint(xs) {
            position: relative;
            .v-input__slot {
                position: relative;
                &::before, &:after {
                    display: none;
                }
            }
        }
    }

</style>

